import { add } from "dinero.js";
import React from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { IProduct, IUserConfigurableBundle } from "../../../models/catalogue";
import { isoWebPageURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getSelectedGift } from "../../../store/selectors";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { notEmpty } from "../../../utils/functional";
import { ZERO } from "../../../utils/money";
import { navigateToURL } from "../../../utils/urls";
import { ConfigureGiftStatus } from "../constants";
import {
    setAddToBasketErrorModalState,
    setConfigureGiftStatus,
} from "../reducers";
import { IGiftSelection } from "../reducers.interfaces";
import {
    addBasketLines,
    loadUserConfigurableBundles,
    reloadBasketIfOutdated,
} from "../thunks";
import { ConfigureGiftsWithPurchase as Component } from "./ConfigureGiftsWithPurchaseContent";

const getSelectedProductVariants = (
    bundles: readonly IUserConfigurableBundle[],
    gifts: { [giftID: string]: IGiftSelection },
    ignoreUnavailable = false,
): IProduct[] => {
    // Figure out all the product variants we need to add to the basket
    return Object.values(gifts)
        .map((giftConfig) => {
            try {
                return getSelectedGift(bundles, giftConfig);
            } catch (e) {
                if (ignoreUnavailable) {
                    console.error(e);
                    return null;
                }
                throw e;
            }
        })
        .filter(notEmpty);
};

const getTotalValue = (
    bundles: readonly IUserConfigurableBundle[],
    gifts: { [giftID: string]: IGiftSelection },
) => {
    const selectedVariants = getSelectedProductVariants(bundles, gifts, true);
    const totalValue = selectedVariants
        .map((p) => {
            return getProductPrice(p.price, {
                priceType: PriceType.RETAIL,
                includePostDiscountAddons: true,
                quantity: 1,
            });
        })
        .reduce((memo, price) => {
            return add(memo, price);
        }, ZERO);
    return totalValue;
};

export const ConfigureGiftsWithPurchase = (): React.ReactNode => {
    const status = useAppSelector((state) => state.checkout.gifts.status);
    const bundles = useAppSelector(
        (state) => state.checkout.gifts.userConfigurableBundles,
    );
    const addToBasketError = useAppSelector(
        (state) => state.checkout.gifts.addToBasketError,
    );
    const gifts = useAppSelector((state) => state.checkout.gifts.gifts);

    const dispatch = useAppDispatch();

    const previousGiftsRef = React.useRef(gifts);
    const previousBundlesRef = React.useRef(bundles);

    const loadBundleData = async () => {
        // Mark that loading has started
        dispatch(setConfigureGiftStatus(ConfigureGiftStatus.LOADING));
        // Load the basket
        const { basket: loadedBasket } = await dispatch(
            reloadBasketIfOutdated(),
        ).unwrap();
        // Load user configurable bundles for each line in the basket
        await dispatch(
            loadUserConfigurableBundles({ basket: loadedBasket }),
        ).unwrap();
    };

    const onCloseErrorModal = () => {
        dispatch(setAddToBasketErrorModalState({ isOpen: false, reason: "" }));
    };

    const onAddToBasket = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        // Set new status in Redux
        dispatch(setConfigureGiftStatus(ConfigureGiftStatus.ADDING_TO_BASKET));

        // Figure out all the product variants we need to add to the basket
        let variants: IProduct[] = [];
        try {
            variants = getSelectedProductVariants(bundles, gifts);
        } catch {
            // Message to the user that they have a configuration error
            dispatch(
                setConfigureGiftStatus(ConfigureGiftStatus.SELECTION_ERROR),
            );
            return;
        }

        // Actually add everything to the basket
        const lines = variants.map((variant) => {
            return {
                productURL: variant.url,
                qty: 1,
            };
        });

        try {
            await dispatch(addBasketLines({ lines })).unwrap();

            // Update redux to show that the basket mutation is complete
            dispatch(setConfigureGiftStatus(ConfigureGiftStatus.COMPLETE));

            // Forward the user to their basket
            navigateToURL(isoWebPageURL.wrap(reverse("basket:summary")));
        } catch (e) {
            // Display the error to the user
            let reason = t`An unexpected error occurred. Please try again.`;
            try {
                reason = e.response.body.reason;
            } catch {
                // Ignore error parsing failure
            }
            dispatch(setAddToBasketErrorModalState({ isOpen: true, reason }));
        }
    };

    React.useEffect(() => {
        void loadBundleData();
    }, []);

    React.useEffect(() => {
        const giftsChanged = previousGiftsRef.current !== gifts;
        const bundlesChanged = previousBundlesRef.current !== bundles;

        previousGiftsRef.current = gifts;
        previousBundlesRef.current = bundles;

        // Check Availability and update ConfigureGiftStatus to remove the configuration error message when a user
        // changes their selection to a product that is available.
        const currentlyHasSelectionErr =
            status === ConfigureGiftStatus.SELECTION_ERROR;
        if (!currentlyHasSelectionErr || (!giftsChanged && !bundlesChanged)) {
            return;
        }

        const selectedVariants = getSelectedProductVariants(
            bundles,
            gifts,
            true,
        );
        const allVariantsAvailable = selectedVariants.reduce<boolean>(
            (memo, p) => {
                return memo && p.availability.is_available_to_buy;
            },
            true,
        );
        if (allVariantsAvailable) {
            dispatch(setConfigureGiftStatus(ConfigureGiftStatus.LOADED));
        }
    }, [gifts, bundles, status, dispatch]);

    if (status === ConfigureGiftStatus.LOADING) {
        return <LoadingSpinner />;
    }

    return (
        <Component
            status={status}
            bundles={bundles}
            onAddToBasket={onAddToBasket}
            addToBasketErrorOpen={addToBasketError.addToBasketErrorOpen}
            addToBasketErrorReason={addToBasketError.addToBasketErrorReason}
            onCloseErrorModal={onCloseErrorModal}
            totalValue={getTotalValue(bundles, gifts)}
        />
    );
};
